import { axiosInstance } from '@/lib/axios';
import { getCsrfToken, isCsrfFailure, refreshCsrfToken } from '@/lib/csrf';
import { getDeviceId } from '@/lib/deviceId';
import { adminListParams, trackerParams } from './console';

const BASE = '/api/fleet/milo';
const ECHO_BACKEND_URL = import.meta.env.VITE_ECHO_BACKEND_URL ?? '';

export type PausedReason = 'monthly' | 'unconfigured' | 'off';

export interface MiloMe {
  /** Configured and switched on */
  available: boolean;
  enabled: boolean;
  configured: boolean;
  remainingToday: number;
  dailyLimit: number;
  paused: boolean;
  pausedReason: PausedReason | null;
}

export interface ConversationSummary {
  id: string;
  title: string;
  updatedAt: string;
}

export interface StoredConversation {
  id: string;
  title: string;
  messages: Array<{ id: string; role: 'user' | 'assistant'; parts: unknown[] }>;
}

export type MiloEffort = 'low' | 'medium' | 'high' | 'xhigh' | 'max';
export type MiloMode = 'strict' | 'free';

export type MiloJob = 'sort' | 'small' | 'normal' | 'deep' | 'title' | 'summary' | 'tracker_check' | 'tracker_decide' | 'tracker_note';
/** One job: the model, its thinking effort and the model that answers when the first is overloaded (null = none) */
export interface MiloJobEntry {
  model: string;
  effort: MiloEffort;
  fallback: string | null;
}
export type MiloJobTable = Record<MiloJob, MiloJobEntry>;

/** USD per 1M tokens */
export interface MiloPrices {
  input: number;
  cacheRead: number;
  cacheWrite5m: number;
  cacheWrite1h: number;
  output: number;
}

export interface MiloCatalogEntry {
  id: string;
  label: string;
  efforts: MiloEffort[];
  prices: MiloPrices;
  note: string | null;
}

export interface MiloSettings {
  enabled: boolean;
  dailyLimit: number;
  monthlyCapUsd: number;
  mode: MiloMode;
  /** Damis AI keeps its own notebook */
  memory: boolean;
  /** The sorter sizes each question; off = every question is normal */
  sorting: boolean;
  jobs: MiloJobTable;
  /** Tura tracking's AI: deciding unclear turas and a note per tura (their models are in the job table) */
  trackerDecisions: boolean;
  trackerNotes: boolean;
  keyConfigured: boolean;
  keyLast4: string | null;
  /** MILO_SECRET is set on the server: without it no key can be stored */
  secretConfigured: boolean;
  updatedAt: string | null;
  catalog: MiloCatalogEntry[];
}

/** apiKey: absent keeps the stored key, '' removes it */
export type MiloSettingsInput = Pick<MiloSettings, 'enabled' | 'dailyLimit' | 'monthlyCapUsd' | 'jobs' | 'sorting' | 'mode' | 'memory' | 'trackerDecisions' | 'trackerNotes'> & { apiKey?: string };

export interface KeyTestResult {
  result: 'ok' | 'invalid' | 'no_access' | 'unreachable' | 'no_key';
  status: number | null;
}

export interface MiloUsage {
  month: string;
  questions: number;
  costUsd: number;
  byModel: Array<{ model: string; label: string; questions: number; costUsd: number; inputTokens: number; cacheReadTokens: number; outputTokens: number }>;
  byUser: Array<{ userId: string; name: string; questions: number; costUsd: number }>;
  /** Chat, and tura tracking's decisions and notes: every call, failed ones too */
  byPurpose: Array<{ purpose: 'chat' | 'tracker_decision' | 'tracker_note'; calls: number; costUsd: number }>;
  /** Every call by the job that made it; null = calls from before the job table */
  byJob: Array<{ job: MiloJob | null; calls: number; costUsd: number }>;
}

export const getMiloMe = async (): Promise<MiloMe> => (await axiosInstance.get<{ data: MiloMe }>(`${BASE}/me`)).data.data;
export const getConversations = async (): Promise<ConversationSummary[]> =>
  (await axiosInstance.get<{ data: ConversationSummary[] }>(`${BASE}/conversations`)).data.data;
export const getConversation = async (id: string): Promise<StoredConversation> =>
  (await axiosInstance.get<{ data: StoredConversation }>(`${BASE}/conversations/${id}`)).data.data;
export const deleteConversation = async (id: string): Promise<void> => {
  await axiosInstance.delete(`${BASE}/conversations/${id}`);
};
export const getMiloSettings = async (): Promise<MiloSettings> => (await axiosInstance.get<{ data: MiloSettings }>(`${BASE}/settings`)).data.data;
export const saveMiloSettings = async (input: MiloSettingsInput): Promise<MiloSettings> =>
  (await axiosInstance.put<{ data: MiloSettings }>(`${BASE}/settings`, input)).data.data;
export const testMiloKey = async (): Promise<KeyTestResult> => (await axiosInstance.post<{ data: KeyTestResult }>(`${BASE}/settings/test`)).data.data;
export const getMiloUsage = async (month?: string): Promise<MiloUsage> =>
  (await axiosInstance.get<{ data: MiloUsage }>(`${BASE}/usage`, { params: month ? { month } : {} })).data.data;

/** A note in the admin's notebook; updatedBy = whose question caused its last change */
export interface MiloNoteItem {
  path: string;
  chars: number;
  updatedAt: string;
  updatedBy: { id: string; name: string } | null;
}

export interface MiloNotebookLimits {
  notes: number;
  noteChars: number;
  totalChars: number;
}

/** The notebooks for admins: the guide (code, read only), the company's and every user's own */
export interface MiloNotebook {
  guide: Array<{ name: string; chars: number }>;
  company: { notes: MiloNoteItem[]; totalChars: number };
  users: Array<{ userId: string; name: string; notes: MiloNoteItem[]; totalChars: number }>;
  limits: { company: MiloNotebookLimits; me: MiloNotebookLimits };
}

export interface MiloNoteContent {
  path: string;
  content: string;
  chars: number;
  updatedAt: string | null;
  updatedBy: { id: string; name: string } | null;
}

/** A folder of the admin's notebook: userId goes with 'me' only */
export type NotebookFolder = 'guide' | 'company' | 'me';

export const getMiloNotebook = async (): Promise<MiloNotebook> => (await axiosInstance.get<{ data: MiloNotebook }>(`${BASE}/admin/notes`)).data.data;
export const getNotebookNote = async (folder: NotebookFolder, path: string, userId?: string): Promise<MiloNoteContent> =>
  (await axiosInstance.get<{ data: MiloNoteContent }>(`${BASE}/admin/notes/item`, { params: { folder, path, ...(userId ? { userId } : {}) } })).data.data;
export const deleteNotebookNote = async (folder: Exclude<NotebookFolder, 'guide'>, path: string, userId?: string): Promise<void> => {
  await axiosInstance.delete(`${BASE}/admin/notes/item`, { params: { folder, path, ...(userId ? { userId } : {}) } });
};
/** Deletes every note of the company's folder, or of one user (folder 'me' with a userId) */
export const wipeNotebook = async (folder: Exclude<NotebookFolder, 'guide'>, userId?: string): Promise<void> => {
  await axiosInstance.delete(`${BASE}/admin/notes`, { params: { folder, ...(userId ? { userId } : {}) } });
};

/** The long chat goes on in a new one that starts from a summary of the old */
export const continueConversation = async (id: string): Promise<{ conversationId: string; title: string }> =>
  (await axiosInstance.post<{ data: { conversationId: string; title: string } }>(`${BASE}/conversations/${id}/continue`)).data.data;

/** What Damis AI remembers about the signed-in user alone */
export interface MyNotes {
  notes: Array<{ path: string; chars: number; updatedAt: string }>;
  totalChars: number;
  limits: { notes: number; noteChars: number; totalChars: number };
}
export const getMyNotes = async (): Promise<MyNotes> => (await axiosInstance.get<{ data: MyNotes }>(`${BASE}/notes`)).data.data;
export const getMyNote = async (path: string): Promise<{ path: string; content: string; chars: number; updatedAt: string }> =>
  (await axiosInstance.get<{ data: { path: string; content: string; chars: number; updatedAt: string } }>(`${BASE}/notes/item`, { params: { path } })).data.data;
export const deleteMyNote = async (path: string): Promise<void> => {
  await axiosInstance.delete(`${BASE}/notes/item`, { params: { path } });
};
export const wipeMyNotes = async (): Promise<void> => {
  await axiosInstance.delete(`${BASE}/notes`);
};

export type TurnStatus = 'ok' | 'error' | 'aborted';

/** A conversation in the admins' list: every attempt summed; the model and the status are its last answer's */
export interface AdminConversationItem {
  id: string;
  userId: string;
  userName: string;
  /** '' once the owner deleted it, or once its first question failed */
  title: string;
  deleted: boolean;
  startedAt: string;
  lastAt: string;
  turns: number;
  inputTokens: number;
  cacheReadTokens: number;
  outputTokens: number;
  reasoningTokens: number;
  costUsd: number;
  durationMs: number;
  model: string;
  modelLabel: string;
  fallback: boolean;
  errors: number;
  status: TurnStatus;
}

export interface AdminConversationsPage {
  items: AdminConversationItem[];
  nextCursor: string | null;
  /** Everyone who has a conversation, for the filter */
  users: Array<{ id: string; name: string }>;
}

export interface AdminToolCall {
  name: string;
  input: unknown;
  output: unknown;
  error: boolean;
}

/** One question's numbers (its attempts summed) and its answer's tool calls */
export interface AdminTurn {
  messageId: string | null;
  createdAt: string;
  model: string;
  modelLabel: string;
  effort: string;
  /** The job that answered (small, normal or deep); null for rows from before the job table */
  job: MiloJob | null;
  fallback: boolean;
  status: TurnStatus;
  inputTokens: number;
  cacheReadTokens: number;
  outputTokens: number;
  reasoningTokens: number;
  costUsd: number;
  durationMs: number;
  timeline: string;
  tools: AdminToolCall[];
}

export interface AdminConversationDetail {
  id: string;
  userId: string;
  userName: string;
  title: string;
  deleted: boolean;
  startedAt: string;
  messages: StoredConversation['messages'];
  turns: AdminTurn[];
}

export interface AdminConversationFilter {
  user: string | null;
  /** Local days, YYYY-MM-DD; '' for none */
  from: string;
  to: string;
  status: TurnStatus | null;
}

export type TrackerPurpose = 'tracker_decision' | 'tracker_note';

/** One call the tracker made to Claude: for which tura, what Claude said and what came of it, what it cost */
export interface TrackerCall {
  id: number;
  at: string;
  purpose: TrackerPurpose;
  /** The tracker job that made the call; null for rows from before the job table */
  job: MiloJob | null;
  /** null when the tura was deleted */
  load: { id: string; number: number } | null;
  model: string;
  modelLabel: string;
  inputTokens: number;
  cacheReadTokens: number;
  outputTokens: number;
  costUsd: number;
  durationMs: number;
  status: TurnStatus;
  /** null for calls made before the log kept it */
  detail: Record<string, unknown> | null;
}

export interface TrackerCallFilter {
  /** Local days, YYYY-MM-DD; '' for none */
  from: string;
  to: string;
  purpose: TrackerPurpose | null;
  status: TurnStatus | null;
}

export interface TrackerCallsPage {
  items: TrackerCall[];
  nextCursor: number | null;
  /** Every call the filter matches */
  totals: { calls: number; costUsd: number };
}

export const getTrackerCalls = async (filter: TrackerCallFilter, cursor?: number): Promise<TrackerCallsPage> =>
  (await axiosInstance.get<{ data: TrackerCallsPage }>(`${BASE}/admin/tracker-calls`, { params: trackerParams(filter, cursor) })).data.data;

export const getAdminConversations = async (filter: AdminConversationFilter, cursor?: string): Promise<AdminConversationsPage> =>
  (await axiosInstance.get<{ data: AdminConversationsPage }>(`${BASE}/admin/conversations`, { params: adminListParams(filter, cursor) })).data.data;
export const getAdminConversation = async (id: string): Promise<AdminConversationDetail> =>
  (await axiosInstance.get<{ data: AdminConversationDetail }>(`${BASE}/admin/conversations/${id}`)).data.data;

/** Why a question got no answer at all: the gate's reason, or how the request failed */
export type ChatRefusal = 'busy' | 'daily' | 'monthly' | 'unconfigured' | 'not_enabled' | 'not_found' | 'session' | 'network' | 'failed';

export interface ChatRequest {
  conversationId: string | null;
  text: string;
  context: { path: string; language: 'sr' | 'en' | 'es'; timezone: string };
}

const REFUSALS: ChatRefusal[] = ['busy', 'daily', 'monthly', 'unconfigured', 'not_enabled', 'not_found'];

async function refusalOf(response: Response): Promise<ChatRefusal> {
  if (response.status === 401) return 'session';
  const body = (await response.json().catch(() => null)) as { reason?: string; error?: string } | null;
  const code = (body?.reason ?? body?.error) as ChatRefusal | undefined;
  return code && REFUSALS.includes(code) ? code : 'failed';
}

/**
 * Sends a question; the answer streams back. fetch, not axios: axios cannot hand over a stream in the browser. The
 * headers are the ones axios sends (lib/axios.ts): the session is bound to this device, and writes carry the CSRF token.
 */
export async function sendChat(request: ChatRequest, signal: AbortSignal): Promise<{ ok: true; stream: ReadableStream<Uint8Array> } | { ok: false; refusal: ChatRefusal }> {
  const post = () => {
    const deviceId = getDeviceId();
    const csrf = getCsrfToken();
    return fetch(`${ECHO_BACKEND_URL}${BASE}/chat`, {
      method: 'POST',
      credentials: 'include',
      signal,
      headers: { 'content-type': 'application/json', ...(deviceId ? { 'x-device-id': deviceId } : {}), ...(csrf ? { 'x-csrf-token': csrf } : {}) },
      body: JSON.stringify(request),
    });
  };
  let response: Response;
  try {
    response = await post();
    if (response.status === 403 && isCsrfFailure(await response.clone().json().catch(() => null))) {
      await refreshCsrfToken();
      response = await post();
    }
  } catch (error) {
    if ((error as Error).name === 'AbortError') throw error;
    return { ok: false, refusal: 'network' };
  }
  if (response.ok && response.body) return { ok: true, stream: response.body };
  return { ok: false, refusal: await refusalOf(response) };
}
